import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CalendarClock,
  Clock,
  Plus,
  Repeat,
  Trash2,
  Users,
  Zap,
} from 'lucide-react';
import {
  AudienceFilters,
  AudienceSummary,
  countActive,
} from '../../components/AudienceFilters';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { Drawer } from '../../components/Drawer';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  Select,
  Skeleton,
  Textarea,
  cn,
} from '../../components/ui';
import { api } from '../../lib/api';
import { formatDateTime, formatNumber } from '../../lib/format';
import { translate, useT } from '../../lib/i18n';
import { errorMessage, useToast } from '../../lib/toast';
import type {
  AudienceFilter,
  NotificationRule,
  NotificationSchedule,
  Template,
  TriggerCatalogue,
} from '../../lib/types';

const ALL_DAYS = [1, 2, 3, 4, 5, 6, 7];
const DEFAULT_TIME_ZONE = 'Europe/Istanbul';

/**
 * A late push is worse than none — "good night" at 9 a.m. — so a new
 * recurring case starts with an hour's grace rather than none.
 */
const RECURRING_DEFAULT_EXPIRY = '60';

/**
 * The cases.
 *
 * A case is one sentence: *when this happens, wait this long, and if they
 * still look like this, send them that.* The three parts are independent, and
 * that is what makes the set expressive without any of it being special-cased
 * in the backend — a pair of opposite messages is two cases on the same
 * trigger whose conditions differ by one field.
 */
export function CasesTab() {
  const t = useT();
  const [editing, setEditing] = useState<NotificationRule | 'new' | null>(null);

  const rules = useQuery({
    queryKey: ['rules'],
    queryFn: () => api.get<NotificationRule[]>('/api/notifications/rules'),
  });

  return (
    <>
      <div className="space-y-4">
        <Card>
          <CardHeader
            title={t('notif.cases.title')}
            description={t('notif.cases.description')}
            action={
              <Button
                variant="primary"
                icon={<Plus size={15} />}
                onClick={() => setEditing('new')}
              >
                {t('notif.cases.new')}
              </Button>
            }
          />
        </Card>

        {rules.isError && <ErrorNote error={rules.error} />}

        {rules.isPending ? (
          <Skeleton className="h-64" />
        ) : (rules.data?.length ?? 0) === 0 ? (
          <Card>
            <EmptyState
              icon={<Zap size={28} />}
              title={t('notif.cases.emptyTitle')}
              description={t('notif.cases.emptyDescription')}
              action={
                <Button variant="primary" onClick={() => setEditing('new')}>
                  {t('notif.cases.emptyAction')}
                </Button>
              }
            />
          </Card>
        ) : (
          <div className="space-y-3">
            {rules.data?.map((rule) => (
              <RuleCard
                key={rule.id}
                rule={rule}
                onEdit={() => setEditing(rule)}
              />
            ))}
          </div>
        )}
      </div>

      {editing && (
        <RuleEditor
          rule={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
        />
      )}
    </>
  );
}

function RuleCard({
  rule,
  onEdit,
}: {
  rule: NotificationRule;
  onEdit: () => void;
}) {
  const t = useT();
  const triggers = useQuery({
    queryKey: ['triggers'],
    queryFn: () => api.get<TriggerCatalogue>('/api/notifications/triggers'),
    staleTime: Infinity,
  });

  const definition = triggers.data?.triggers.find(
    (item) => item.trigger === rule.trigger,
  );

  return (
    <Card>
      <button
        type="button"
        onClick={onEdit}
        className="w-full px-4 py-3.5 text-left transition-colors hover:bg-raised sm:px-5"
      >
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-ink">{rule.name}</span>
            {!rule.enabled && <Badge>{t('common.disabled')}</Badge>}
            {rule.isRecurring ? (
              <Badge tone="good" icon={<CalendarClock size={11} />}>
                {t('notif.cases.recurring')}
              </Badge>
            ) : rule.isScheduled ? (
              <Badge tone="warn" icon={<Repeat size={11} />}>
                {t('notif.cases.swept')}
              </Badge>
            ) : (
              <Badge tone="brand" icon={<Zap size={11} />}>
                {t('notif.cases.event')}
              </Badge>
            )}
          </div>
          <span className="tnum text-xs text-ink-3">
            {t('notif.cases.queuedSoFar', {
              count: formatNumber(rule.stats.total ?? 0),
            })}
          </span>
        </div>

        {/* The case, spelled out as the sentence it is. */}
        <p className="mt-1.5 text-xs text-ink-2">
          <span className="font-medium text-ink">
            {rule.schedule
              ? describeSchedule(rule.schedule)
              : (definition?.label ?? rule.trigger)}
          </span>
          {rule.delayMinutes > 0 && (
            <>
              {` → ${t('notif.cases.wait')} `}
              <span className="font-medium text-ink">
                {describeDelay(rule.delayMinutes)}
              </span>
            </>
          )}
          {` → ${t('notif.cases.send')} `}
          <span className="font-medium text-ink">
            {rule.templateName ?? t('notif.cases.deletedTemplate')}
          </span>
        </p>

        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-ink-3">
          <span className="inline-flex items-center gap-1">
            <Users size={12} />
            <AudienceSummary filter={rule.conditions} />
          </span>
          {rule.cooldownHours && (
            <span className="inline-flex items-center gap-1">
              <Clock size={12} />{' '}
              {t('notif.cases.cooldown', { hours: rule.cooldownHours })}
            </span>
          )}
          {rule.isRecurring && rule.enabled && rule.nextRunAt && (
            <span className="tnum inline-flex items-center gap-1">
              <CalendarClock size={12} />
              {t('notif.cases.nextRun', {
                time: formatDateTime(rule.nextRunAt),
              })}
            </span>
          )}
          {rule.isRecurring && rule.lastRunAt && (
            <span className="tnum">
              {t('notif.cases.lastRun', {
                time: formatDateTime(rule.lastRunAt),
              })}
            </span>
          )}
        </div>
      </button>
    </Card>
  );
}

/** "Every day at 22:00 (Europe/Istanbul)" — the schedule as a phrase. */
function describeSchedule(schedule: NotificationSchedule): string {
  const days = [...schedule.days].sort().join();
  const when =
    days === ALL_DAYS.join()
      ? translate('notif.cases.everyDay')
      : days === '1,2,3,4,5'
        ? translate('notif.cases.weekdays')
        : days === '6,7'
          ? translate('notif.cases.weekends')
          : schedule.days
              .map((day) => translate(`notif.cases.day.${day}`))
              .join(', ');

  return `${when} ${translate('notif.cases.at')} ${schedule.time} (${schedule.timeZone})`;
}

/**
 * Every zone the browser knows, with the default pinned first. The native
 * select's type-ahead makes four hundred options quick to reach.
 */
function timeZones(current: string): string[] {
  let zones: string[];
  try {
    zones = Intl.supportedValuesOf('timeZone');
  } catch {
    zones = [];
  }
  return [
    ...new Set([DEFAULT_TIME_ZONE, current, 'UTC', ...zones].filter(Boolean)),
  ];
}

function describeDelay(minutes: number): string {
  if (minutes < 60) {
    return translate('notif.cases.delayMinutes', { count: minutes });
  }
  if (minutes < 1440) {
    return translate('notif.cases.delayHours', {
      count: Math.round(minutes / 60),
    });
  }
  return translate('notif.cases.delayDays', {
    count: Math.round(minutes / 1440),
  });
}

// ── Editor ──────────────────────────────────────────────────────────────────

function RuleEditor({
  rule,
  onClose,
}: {
  rule: NotificationRule | null;
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<'save' | 'delete' | null>(null);

  const [form, setForm] = useState({
    name: rule?.name ?? '',
    description: rule?.description ?? '',
    enabled: rule?.enabled ?? true,
    trigger: rule?.trigger ?? 'user_registered',
    templateId: rule?.templateId ?? '',
    delayMinutes: String(rule?.delayMinutes ?? 0),
    expiresAfterMinutes: rule?.expiresAfterMinutes
      ? String(rule.expiresAfterMinutes)
      : '',
    cooldownHours: rule?.cooldownHours ? String(rule.cooldownHours) : '',
    maxPerRun: rule?.maxPerRun ? String(rule.maxPerRun) : '',
    priority: String(rule?.priority ?? 100),
    scheduleTime: rule?.schedule?.time ?? '22:00',
    scheduleZone: rule?.schedule?.timeZone ?? DEFAULT_TIME_ZONE,
    scheduleDays: rule?.schedule?.days ?? ALL_DAYS,
  });
  const [conditions, setConditions] = useState<AudienceFilter>(
    rule?.conditions ?? {},
  );

  const triggers = useQuery({
    queryKey: ['triggers'],
    queryFn: () => api.get<TriggerCatalogue>('/api/notifications/triggers'),
    staleTime: Infinity,
  });

  const templates = useQuery({
    queryKey: ['templates'],
    queryFn: () => api.get<Template[]>('/api/notifications/templates'),
  });

  const preview = useQuery({
    queryKey: ['rule-preview', rule?.id],
    queryFn: () =>
      api.post<{ count: number; swept: boolean }>(
        `/api/notifications/rules/${rule!.id}/preview`,
      ),
    enabled: !!rule,
  });

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const definition = triggers.data?.triggers.find(
    (item) => item.trigger === form.trigger,
  );
  const isSwept = definition?.kind === 'swept';
  const isRecurring = definition?.kind === 'recurring';

  const changeTrigger = (trigger: string) => {
    const next = triggers.data?.triggers.find(
      (item) => item.trigger === trigger,
    );
    setForm((current) => ({
      ...current,
      trigger,
      expiresAfterMinutes:
        next?.kind === 'recurring' && !current.expiresAfterMinutes
          ? RECURRING_DEFAULT_EXPIRY
          : current.expiresAfterMinutes,
    }));
  };

  const toggleDay = (day: number) =>
    set(
      'scheduleDays',
      form.scheduleDays.includes(day)
        ? form.scheduleDays.filter((item) => item !== day)
        : [...form.scheduleDays, day].sort(),
    );

  const save = useMutation({
    mutationFn: (reason: string) => {
      const body = {
        name: form.name,
        description: form.description || undefined,
        enabled: form.enabled,
        trigger: form.trigger,
        templateId: form.templateId,
        // The schedule is the "when" of a recurring case; a delay on top of
        // it would only be a second way of saying the same time.
        delayMinutes: isRecurring ? 0 : Number(form.delayMinutes) || 0,
        expiresAfterMinutes: form.expiresAfterMinutes
          ? Number(form.expiresAfterMinutes)
          : undefined,
        conditions,
        cooldownHours:
          form.cooldownHours && !isRecurring
            ? Number(form.cooldownHours)
            : undefined,
        maxPerRun: form.maxPerRun ? Number(form.maxPerRun) : undefined,
        priority: Number(form.priority),
        schedule: isRecurring
          ? {
              time: form.scheduleTime,
              timeZone: form.scheduleZone,
              days: form.scheduleDays,
            }
          : undefined,
        reason,
      };

      return rule
        ? api.patch(`/api/notifications/rules/${rule.id}`, body)
        : api.post('/api/notifications/rules', body);
    },
    onSuccess: () => {
      toast.success(t(rule ? 'notif.cases.updated' : 'notif.cases.created'));
      void queryClient.invalidateQueries({ queryKey: ['rules'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (reason: string) =>
      api.delete(`/api/notifications/rules/${rule!.id}`, { reason }),
    onSuccess: () => {
      toast.success(t('notif.cases.deleted'));
      void queryClient.invalidateQueries({ queryKey: ['rules'] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const conditionsEmpty = countActive(conditions) === 0;

  return (
    <Drawer
      open
      onClose={onClose}
      title={
        rule
          ? t('notif.cases.editTitle', { name: rule.name })
          : t('notif.cases.newTitle')
      }
      footer={
        <div className="flex gap-2">
          {rule && (
            <Button
              variant="danger"
              icon={<Trash2 size={15} />}
              onClick={() => setDialog('delete')}
            >
              {t('common.delete')}
            </Button>
          )}
          <Button
            variant="primary"
            className="flex-1"
            disabled={
              !form.name.trim() ||
              !form.templateId ||
              (isSwept && conditionsEmpty) ||
              (isRecurring &&
                (!form.scheduleTime || form.scheduleDays.length === 0))
            }
            onClick={() => setDialog('save')}
          >
            {rule ? t('common.saveChanges') : t('notif.cases.create')}
          </Button>
        </div>
      }
    >
      <div className="space-y-5">
        <section className="space-y-3">
          <Field label={t('common.name')}>
            <Input
              value={form.name}
              onChange={(event) => set('name', event.target.value)}
              placeholder={t('notif.cases.namePlaceholder')}
            />
          </Field>
          <Field label={t('common.description')}>
            <Textarea
              rows={2}
              value={form.description}
              onChange={(event) => set('description', event.target.value)}
            />
          </Field>
          <Field label={t('common.enabled')}>
            <Select
              value={String(form.enabled)}
              onChange={(event) => set('enabled', event.target.value === 'true')}
            >
              <option value="true">{t('common.yes')}</option>
              <option value="false">{t('notif.cases.enabledNo')}</option>
            </Select>
          </Field>
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
            {t('notif.cases.section.fires')}
          </h3>

          <Field label={t('notif.cases.trigger')} hint={definition?.description}>
            <Select
              value={form.trigger}
              onChange={(event) => changeTrigger(event.target.value)}
            >
              {triggers.data?.triggers.map((item) => (
                <option key={item.trigger} value={item.trigger}>
                  {item.label}
                </option>
              ))}
            </Select>
          </Field>

          {isSwept && (
            <div className="rounded-lg border border-warn/30 bg-warn-soft px-3 py-2 text-xs text-warn">
              {t('notif.cases.sweptWarning')}
            </div>
          )}

          {isRecurring && (
            <ScheduleFields
              time={form.scheduleTime}
              timeZone={form.scheduleZone}
              days={form.scheduleDays}
              onTime={(value) => set('scheduleTime', value)}
              onTimeZone={(value) => set('scheduleZone', value)}
              onToggleDay={toggleDay}
            />
          )}

          <div className="grid gap-3 sm:grid-cols-2">
            {!isRecurring && (
              <Field
                label={t('notif.cases.delay')}
                hint={t('notif.cases.delayHint')}
              >
                <Input
                  type="number"
                  min={0}
                  value={form.delayMinutes}
                  onChange={(event) => set('delayMinutes', event.target.value)}
                />
              </Field>
            )}
            <Field
              label={t('notif.cases.expires')}
              hint={t('notif.cases.expiresHint')}
            >
              <Input
                type="number"
                min={1}
                value={form.expiresAfterMinutes}
                onChange={(event) =>
                  set('expiresAfterMinutes', event.target.value)
                }
                placeholder={t('common.never')}
              />
            </Field>
          </div>
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
            {t('notif.cases.section.sends')}
          </h3>
          <Field label={t('common.template')}>
            <Select
              value={form.templateId}
              onChange={(event) => set('templateId', event.target.value)}
            >
              <option value="">{t('common.selectTemplate')}</option>
              {templates.data?.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.name}
                </option>
              ))}
            </Select>
          </Field>

          {definition && definition.variables.length > 0 && (
            <p className="text-xs text-ink-3">
              {t('notif.cases.fillsAlso')}{' '}
              <code className="font-mono">
                {definition.variables.map((v) => `{{${v}}}`).join(' ')}
              </code>
            </p>
          )}
        </section>

        <section className="space-y-2 border-t border-line pt-4">
          <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
            {t('notif.cases.section.conditions')}
          </h3>
          <p className="text-xs text-ink-3">
            {t('notif.cases.conditionsNote')}
          </p>

          {preview.data && (
            <p className="rounded-lg bg-raised px-2.5 py-1.5 text-xs text-ink-2">
              {t('notif.cases.previewMatch', { count: preview.data.count })}
              {!preview.data.swept && t('notif.cases.previewCaveat')}.
            </p>
          )}

          <AudienceFilters
            value={conditions}
            onChange={setConditions}
            showSearch={false}
          />
        </section>

        <section className="space-y-3 border-t border-line pt-4">
          <h3 className="text-xs font-semibold tracking-wide text-ink-2 uppercase">
            {t('notif.cases.section.limits')}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {!isRecurring && (
              <Field
                label={t('notif.cases.cooldownLabel')}
                hint={t('notif.cases.cooldownHint')}
              >
                <Input
                  type="number"
                  min={1}
                  value={form.cooldownHours}
                  onChange={(event) => set('cooldownHours', event.target.value)}
                  placeholder={t('common.noLimit')}
                />
              </Field>
            )}
            <Field
              label={t('notif.cases.maxPerRun')}
              hint={t('notif.cases.maxPerRunHint')}
            >
              <Input
                type="number"
                min={1}
                value={form.maxPerRun}
                onChange={(event) => set('maxPerRun', event.target.value)}
                placeholder={t('common.noCap')}
              />
            </Field>
          </div>
        </section>
      </div>

      <ConfirmDialog
        open={dialog !== null}
        title={
          dialog === 'delete'
            ? t('notif.cases.deleteTitle', { name: rule?.name ?? '' })
            : rule
              ? t('common.saveTheseChanges')
              : t('notif.cases.createTitle')
        }
        description={t(
          dialog === 'delete'
            ? 'notif.cases.deleteDescription'
            : 'notif.cases.saveDescription',
        )}
        destructive={dialog === 'delete'}
        loading={save.isPending || remove.isPending}
        onCancel={() => setDialog(null)}
        onConfirm={(reason) =>
          dialog === 'delete' ? remove.mutate(reason) : save.mutate(reason)
        }
      />
    </Drawer>
  );
}

/**
 * When a recurring case runs: a time, the zone it is read in, and the days.
 * Days are toggles rather than a multi-select, because "which days" is
 * answered at a glance and a phone-sized drawer has room for seven chips.
 */
function ScheduleFields({
  time,
  timeZone,
  days,
  onTime,
  onTimeZone,
  onToggleDay,
}: {
  time: string;
  timeZone: string;
  days: number[];
  onTime: (value: string) => void;
  onTimeZone: (value: string) => void;
  onToggleDay: (day: number) => void;
}) {
  const t = useT();

  return (
    <div className="space-y-3 rounded-lg border border-line bg-raised/50 p-3">
      <p className="text-xs text-ink-3">{t('notif.cases.scheduleNote')}</p>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label={t('notif.cases.scheduleTime')}>
          <Input
            type="time"
            required
            value={time}
            onChange={(event) => onTime(event.target.value)}
          />
        </Field>
        <Field label={t('notif.cases.scheduleZone')}>
          <Select
            value={timeZone}
            onChange={(event) => onTimeZone(event.target.value)}
          >
            {timeZones(timeZone).map((zone) => (
              <option key={zone} value={zone}>
                {zone}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      {/* A fieldset rather than `Field`: a <label> forwards clicks to its
          first button, so tapping "Days" would toggle Monday. */}
      <fieldset className="space-y-1.5">
        <legend className="text-xs font-medium text-ink-2">
          {t('notif.cases.scheduleDays')}
        </legend>
        <div className="grid grid-cols-7 gap-1.5">
          {ALL_DAYS.map((day) => {
            const on = days.includes(day);
            return (
              <button
                key={day}
                type="button"
                aria-pressed={on}
                onClick={() => onToggleDay(day)}
                className={cn(
                  'h-9 rounded-md border text-xs font-medium transition-colors',
                  on
                    ? 'border-brand bg-brand text-on-brand'
                    : 'border-line bg-surface text-ink-2 hover:bg-raised',
                )}
              >
                {t(`notif.cases.day.${day}`)}
              </button>
            );
          })}
        </div>
        {days.length === 0 && (
          <span className="block text-xs text-danger">
            {t('notif.cases.scheduleDaysRequired')}
          </span>
        )}
      </fieldset>
    </div>
  );
}
